import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useDrawer } from '@/components/ui/drawer'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import {
  billingService,
  formatAmount,
  providerLabel,
  type BillingPlan,
  type SubscriptionSetup,
} from '@/services/billing'
import { tenantsService, type Tenant } from '@/services/tenants'
import { toast } from 'sonner'
import { Copy, ExternalLink, CreditCard, Loader2, CheckCircle2 } from 'lucide-react'

declare global {
  interface Window {
    CollectJS?: {
      configure: (config: Record<string, unknown>) => void
      startPaymentRequest: () => void
    }
  }
}

const DRAWER_ID = 'create-subscription'

interface CreateSubscriptionDrawerProps {
  onSuccess?: () => void
}

/**
 * Two-step subscription creation. Step 1 picks tenant + plan (the tenant's
 * payment processor decides the flow). Step 2 branches on the setup mode the
 * backend returns:
 *  - redirect (Stripe): show the hosted checkout link to copy/send; the
 *    subscription activates via webhook when the customer completes it.
 *  - embedded (NMI): Collect.js hosted card fields render in the drawer; the
 *    single-use payment token completes the subscription immediately. Card
 *    data lives in NMI-hosted iframes and never touches our servers.
 */
export function CreateSubscriptionDrawer({ onSuccess }: CreateSubscriptionDrawerProps) {
  const { closeDrawer } = useDrawer()
  const { broadcast } = useDrawerMessaging(DRAWER_ID)

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [tenantId, setTenantId] = useState('')
  const [planId, setPlanId] = useState('')
  const [creating, setCreating] = useState(false)

  const [subscriptionId, setSubscriptionId] = useState<string | null>(null)
  const [setup, setSetup] = useState<SubscriptionSetup | null>(null)

  // Embedded (NMI) state
  const [collectReady, setCollectReady] = useState(false)
  const [tokenizing, setTokenizing] = useState(false)
  const [completed, setCompleted] = useState(false)

  useEffect(() => {
    Promise.all([tenantsService.list(), billingService.listPlans()])
      .then(([tenantsData, plansData]) => {
        setTenants(tenantsData)
        setPlans(plansData)
      })
      .catch((err) => {
        console.error('Failed to load tenants/plans:', err)
        toast.error('Failed to load tenants or plans')
      })
  }, [])

  const selectedTenant = useMemo(
    () => tenants.find((t) => t.tenant_id === tenantId),
    [tenants, tenantId]
  )
  const selectedPlan = useMemo(() => plans.find((p) => p.id === planId), [plans, planId])

  const handleCreate = async () => {
    if (!tenantId || !planId) {
      toast.error('Select a tenant and a plan')
      return
    }
    setCreating(true)
    try {
      const result = await billingService.createSubscription(tenantId, planId)
      setSubscriptionId(result.subscription.id)
      setSetup(result.setup)
      broadcast('refresh', {})
    } catch (err: unknown) {
      console.error('Failed to create subscription:', err)
      const detail = (err as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail
      toast.error(detail || 'Failed to create subscription')
    } finally {
      setCreating(false)
    }
  }

  // Load Collect.js once the backend hands us the embedded setup.
  useEffect(() => {
    if (setup?.mode !== 'embedded') return

    const existing = document.querySelector<HTMLScriptElement>('script[data-collectjs]')
    if (existing) {
      setCollectReady(true)
      return
    }
    const script = document.createElement('script')
    script.src = setup.script_url
    script.dataset.collectjs = 'true'
    script.setAttribute('data-tokenization-key', setup.tokenization_key)
    script.onload = () => setCollectReady(true)
    script.onerror = () => toast.error('Failed to load the NMI payment form')
    document.body.appendChild(script)
  }, [setup])

  // Configure the hosted card fields when the script is ready.
  useEffect(() => {
    if (setup?.mode !== 'embedded' || !collectReady || !window.CollectJS) return
    window.CollectJS.configure({
      variant: 'inline',
      styleSniffer: true,
      fields: {
        ccnumber: { selector: '#nmi-ccnumber', placeholder: 'Card number' },
        ccexp: { selector: '#nmi-ccexp', placeholder: 'MM / YY' },
        cvv: { selector: '#nmi-cvv', placeholder: 'CVV' },
      },
      callback: async (response: { token?: string }) => {
        if (!response.token) {
          setTokenizing(false)
          toast.error('Card tokenization failed')
          return
        }
        try {
          await billingService.completeEmbeddedSubscription(
            subscriptionId as string,
            response.token
          )
          setCompleted(true)
          toast.success('Subscription activated')
          broadcast('refresh', {})
          onSuccess?.()
        } catch (err: unknown) {
          console.error('Failed to complete subscription:', err)
          const detail = (err as { response?: { data?: { detail?: string } } })
            ?.response?.data?.detail
          toast.error(detail || 'Failed to complete subscription')
        } finally {
          setTokenizing(false)
        }
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup, collectReady, subscriptionId])

  const handleCopyLink = () => {
    if (setup?.mode === 'redirect') {
      navigator.clipboard.writeText(setup.url)
      toast.success('Checkout link copied')
    }
  }

  const handleDone = () => {
    onSuccess?.()
    closeDrawer(DRAWER_ID)
  }

  // ------------------------------------------------------------- rendering

  if (setup?.mode === 'redirect') {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="text-sm font-medium mb-1">Checkout link created</p>
          <p className="text-sm text-muted-foreground">
            Send this link to {selectedTenant?.contact_name || 'the customer'} — the
            subscription activates automatically when they complete checkout. Until
            then it shows as <Badge variant="outline">pending checkout</Badge>.
          </p>
        </div>
        <div className="space-y-2">
          <Label>Hosted checkout URL</Label>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md border bg-muted px-3 py-2 text-xs">
              {setup.url}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopyLink}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(setup.url, '_blank')}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleDone}>Done</Button>
        </div>
      </div>
    )
  }

  if (setup?.mode === 'embedded') {
    if (completed) {
      return (
        <div className="space-y-6 text-center py-8">
          <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
          <div>
            <p className="font-medium">Subscription activated</p>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedTenant?.company_name} is now subscribed to {selectedPlan?.name}.
            </p>
          </div>
          <Button onClick={handleDone}>Done</Button>
        </div>
      )
    }
    return (
      <div className="space-y-6">
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="text-sm font-medium mb-1 flex items-center gap-2">
            <CreditCard className="h-4 w-4" /> Enter card details
          </p>
          <p className="text-sm text-muted-foreground">
            Card fields are hosted by NMI — the card number never touches our
            servers. The subscription activates immediately after the card is
            vaulted.
          </p>
        </div>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Card number</Label>
            <div id="nmi-ccnumber" className="h-10 rounded-md border bg-background px-3" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Expiration</Label>
              <div id="nmi-ccexp" className="h-10 rounded-md border bg-background px-3" />
            </div>
            <div className="space-y-1">
              <Label>CVV</Label>
              <div id="nmi-cvv" className="h-10 rounded-md border bg-background px-3" />
            </div>
          </div>
        </div>
        {!collectReady && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading secure payment form…
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleDone}>
            Cancel
          </Button>
          <Button
            disabled={!collectReady || tokenizing}
            onClick={() => {
              setTokenizing(true)
              window.CollectJS?.startPaymentRequest()
            }}
          >
            {tokenizing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Subscribe {selectedPlan ? formatAmount(selectedPlan.amount_cents) : ''}/
            {selectedPlan?.interval ?? 'month'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Tenant</Label>
        <Select value={tenantId} onValueChange={setTenantId}>
          <SelectTrigger>
            <SelectValue placeholder="Select a tenant..." />
          </SelectTrigger>
          <SelectContent>
            {tenants.map((tenant) => (
              <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                {tenant.company_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedTenant && (
          <p className="text-sm text-muted-foreground">
            Payment processor:{' '}
            <Badge variant="outline">
              {providerLabel(selectedTenant.payment_provider || 'stripe')}
            </Badge>
            {selectedTenant.payment_provider === 'nmi'
              ? ' — card is entered here in the drawer'
              : ' — a hosted checkout link is generated to send to the customer'}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Plan</Label>
        <Select value={planId} onValueChange={setPlanId}>
          <SelectTrigger>
            <SelectValue placeholder="Select a plan..." />
          </SelectTrigger>
          <SelectContent>
            {plans.map((plan) => (
              <SelectItem key={plan.id} value={plan.id}>
                {plan.name} — {formatAmount(plan.amount_cents, plan.currency)}/
                {plan.interval}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {plans.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No plans yet — create one via “Manage Plans” on the Subscriptions page.
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => closeDrawer(DRAWER_ID)}>
          Cancel
        </Button>
        <Button onClick={handleCreate} disabled={creating || !tenantId || !planId}>
          {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create Subscription
        </Button>
      </div>
    </div>
  )
}
